import { PlusIcon, Trash01Icon } from '@langchain/untitled-ui-icons';
import { Checkbox } from '@mui/joy';

import { useCallback, useMemo } from 'react';

import { InfoTooltip } from '@/components/InfoTooltip/InfoTooltip';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/Select/Select';
import { Button, IconButton, Input, Text } from '@/design-system';

import { Attribute, InsightsJobFormData } from '../types';
import { ErrorText, FormSection } from './ConfigSectionComponents';

const TypeBanner = ({ type }: { type?: string }) => {
  if (!type) {
    return null;
  }

  const TYPE_TEXT = {
    string: (
      <Text variant="md" color="secondary" weight="medium">
        This attribute will provide a{' '}
        <Text variant="md" color="secondary" weight="semibold">
          text
        </Text>{' '}
        response based on the feedback criteria.
      </Text>
    ),
    number: (
      <Text variant="md" color="secondary" weight="medium">
        This attribute will provide a{' '}
        <Text variant="md" color="secondary" weight="semibold">
          numerical
        </Text>{' '}
        response based on the feedback criteria.
      </Text>
    ),
    boolean: (
      <Text variant="md" color="secondary" weight="medium">
        This attribute will provide a{' '}
        <Text variant="md" color="secondary" weight="semibold">
          true
        </Text>{' '}
        or{' '}
        <Text variant="md" color="secondary" weight="semibold">
          false
        </Text>{' '}
        response based on the feedback criteria.
      </Text>
    ),
  };

  return (
    <div className="flex w-full items-center rounded-md bg-tertiary px-3 py-2">
      <Text variant="md" color="secondary" weight="medium">
        {TYPE_TEXT[type]}
      </Text>
    </div>
  );
};

const AttributeEditor = ({
  attribute,
  updateAttribute,
  removeAttribute,
}: {
  attribute: Attribute;
  updateAttribute: (attribute: Attribute) => void;
  removeAttribute: () => void;
}) => {
  return (
    <div className="flex flex-col gap-3 rounded-md border border-secondary p-4">
      <Input
        label="Name"
        placeholder="e.g. satisfaction"
        value={attribute.name}
        onChange={(value) => updateAttribute({ ...attribute, name: value })}
        className="text-sm"
      />
      <Input
        label="Description"
        placeholder="e.g. Whether user was satisfied"
        value={attribute.description}
        onChange={(description) =>
          updateAttribute({ ...attribute, description })
        }
        className="text-sm"
      />
      <div className="flex items-center gap-3">
        <Select
          value={attribute.type}
          onValueChange={(type: 'string' | 'number' | 'boolean') =>
            updateAttribute({ ...attribute, type })
          }
        >
          <SelectTrigger>
            <Text variant="sm" color="secondary" weight="medium">
              Type
            </Text>
            <div className="rounded-md bg-quaternary px-1 py-px text-xs font-medium text-secondary">
              <SelectValue placeholder="Select a type" />
            </div>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="string">String</SelectItem>
            <SelectItem value="number">Number</SelectItem>
            <SelectItem value="boolean">Boolean</SelectItem>
          </SelectContent>
        </Select>
        {attribute.type === 'boolean' && (
          <div className="flex items-center gap-2">
            <Checkbox
              size="sm"
              checked={attribute.filter_by ?? false}
              onChange={(e) =>
                updateAttribute({ ...attribute, filter_by: e.target.checked })
              }
              label="Filter"
            />
            <InfoTooltip description="If enabled, runs marked true for this attribute are included in the insights job, while those marked false are excluded." />
          </div>
        )}
      </div>
      <div className="flex items-center justify-between gap-2">
        <TypeBanner type={attribute.type} />
        <div className="ml-auto">
          <IconButton
            label="Remove attribute"
            variant="plain"
            color="error"
            onClick={() => removeAttribute()}
            icon={Trash01Icon}
          />
        </div>
      </div>
    </div>
  );
};

export const AttributesSection = ({
  formData,
  updateFormData,
  errors,
}: {
  formData: InsightsJobFormData;
  updateFormData: (updates: Partial<InsightsJobFormData>) => void;
  errors: Partial<Record<keyof InsightsJobFormData, string>>;
}) => {
  const attributes = useMemo(
    () => formData.attributes || [],
    [formData.attributes]
  );

  const addAttribute = useCallback(() => {
    updateFormData({
      attributes: [
        ...attributes,
        { name: '', description: '', type: 'boolean', filter_by: false },
      ],
    });
  }, [attributes, updateFormData]);

  const removeAttribute = useCallback(
    (index: number) => {
      updateFormData({
        attributes: attributes.filter((_, i) => i !== index),
      });
    },
    [attributes, updateFormData]
  );

  const updateAttribute = useCallback(
    (index: number, attribute: Attribute) => {
      updateFormData({
        attributes: attributes.map((attr, i) =>
          i === index ? attribute : attr
        ),
      });
    },
    [attributes, updateFormData]
  );

  return (
    <FormSection
      title="Add attributes"
      titleVariant="h2"
      description="Define custom attributes to enrich your insights. Attributes can capture things like sentiment, satisfaction, or other key properties."
    >
      {attributes.map((attribute, index) => (
        <AttributeEditor
          key={index}
          attribute={attribute}
          updateAttribute={(attribute) => updateAttribute(index, attribute)}
          removeAttribute={() => removeAttribute(index)}
        />
      ))}
      <Button
        variant="underlined"
        size="sm"
        color="secondary"
        onClick={addAttribute}
        leftDecorator={PlusIcon}
        className="mr-auto"
      >
        Add Attribute
      </Button>
      <ErrorText error={errors.attributes} />
    </FormSection>
  );
};
